import React, { useEffect, useState, useMemo } from "react";
import { getApiUrl } from "../../../../../../../../config/api";

// ── helpers ───────────────────────────────────────────────────────────────────

const UNIT_OPTIONS = [
    { value: "h", label: "Hours", labelZh: "小時" },
    { value: "d", label: "Days", labelZh: "天" },
    { value: "w", label: "Weeks", labelZh: "週" },
];

const buildDuration = (amount, unit) => `${amount}${unit}`;

function addDuration(date, amount, unit) {
    const ms = { h: 3600000, d: 86400000, w: 604800000 }[unit] || 0;
    return new Date(date.getTime() + ms * Number(amount));
}

function fmtDate(date) {
    return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
}

function toInputValue(date) {
    const pad = (n) => String(n).padStart(2, "0");
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
}

// ── HopTimeline ───────────────────────────────────────────────────────────────

function HopTimeline({ startDate, amount, unit, language }) {
    const VISIBLE_HOPS = 2;

    const hops = useMemo(() => {
        if (!startDate || !amount || amount < 1) return [];
        const result = [];
        let cursor = new Date(startDate);
        for (let i = 0; i <= VISIBLE_HOPS; i++) {
            result.push({ index: i, date: new Date(cursor) });
            cursor = addDuration(cursor, Number(amount), unit);
        }
        return result;
    }, [startDate, amount, unit]);

    if (hops.length === 0) return null;

    const unitLabel = (n) => {
        const base = UNIT_OPTIONS.find((u) => u.value === unit);
        if (!base) return "";
        if (language === "zh") return base.labelZh;
        return n === 1 ? base.label.replace(/s$/, "") : base.label;
    };

    return (
        <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                {language === "zh" ? "同步視窗預覽" : "Sync Window Preview"}
            </p>
            <div className="overflow-x-auto pb-2">
                <div className="flex items-start justify-center gap-0 min-w-max">
                    {hops.map((hop, idx) => (
                        <div key={idx} className="flex items-start">
                            {/* Node + label */}
                            <div className="flex flex-col items-center">
                                <div
                                    className={`w-3 h-3 rounded-full border-2 flex-shrink-0 ${
                                        hop.index === 0
                                            ? "bg-orange-500 border-orange-500"
                                            : "bg-white border-orange-400"
                                    }`}
                                />
                                <div className="mt-1.5 text-center w-24">
                                    <span
                                        className={`text-[10px] leading-snug block ${
                                            hop.index === 0
                                                ? "font-semibold text-orange-600"
                                                : "text-gray-500"
                                        }`}
                                    >
                                        {fmtDate(hop.date)}
                                    </span>
                                    {hop.index === 0 && (
                                        <span className="block text-[9px] text-orange-400 font-medium mt-0.5">
                                            {language === "zh"
                                                ? "起點"
                                                : "Start"}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {idx < hops.length - 1 && (
                                <div
                                    className="flex flex-col items-center mx-1"
                                    style={{ width: 72 }}
                                >
                                    <div className="flex items-center w-full mt-1">
                                        <div className="flex-1 h-px bg-orange-200" />
                                        <svg
                                            className="w-2 h-2 text-orange-300 flex-shrink-0"
                                            fill="currentColor"
                                            viewBox="0 0 8 8"
                                        >
                                            <path d="M0 4l4-4v8z" />
                                        </svg>
                                    </div>
                                    <span className="text-[9px] text-orange-400 mt-1 whitespace-nowrap">
                                        +{amount} {unitLabel(Number(amount))}
                                    </span>
                                </div>
                            )}
                            {idx === hops.length - 1 && (
                                <div className="flex items-center ml-2 mt-0.5">
                                    <span className="text-gray-300 text-sm font-bold tracking-widest">
                                        ···
                                    </span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            {/* Current time marker */}
            <div className="mt-3 flex items-center gap-2 text-[11px] text-gray-400">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
                {language === "zh" ? "現在時間：" : "Current time: "}
                <span className="text-gray-600 font-medium">
                    {fmtDate(new Date())}
                </span>
            </div>
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AutomaticPinningView({
    channel,
    language,
    currentUser,
}) {
    const [startDate, setStartDate] = useState(() => toInputValue(new Date()));
    const [hopAmount, setHopAmount] = useState(7);
    const [hopUnit, setHopUnit] = useState("d");

    const [isEnabled, setIsEnabled] = useState(false);
    const [savedConfig, setSavedConfig] = useState(null);

    const [saving, setSaving] = useState(false);
    const [disabling, setDisabling] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        if (channel?.time_sync_duration && channel?.last_sync) {
            setIsEnabled(true);
            setSavedConfig({
                time_sync_duration: channel.time_sync_duration,
                last_sync: channel.last_sync,
            });
            const match = channel.time_sync_duration.match(/^(\d+)([hdw])$/);
            if (match) {
                setHopAmount(Number(match[1]));
                setHopUnit(match[2]);
            }
            setStartDate(toInputValue(new Date(channel.last_sync)));
        }
    }, [channel]);

    const previewStartDate = useMemo(() => {
        const d = new Date(startDate);
        return isNaN(d.getTime()) ? null : d;
    }, [startDate]);

    const token = currentUser?.token || currentUser?.accessToken;

    const callSyncConfig = async (body) => {
        const res = await fetch(
            `${getApiUrl()}/api/v2/channels/${channel.channel_id}/sync-config`,
            {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            },
        );
        const json = await res.json();
        if (!res.ok || !json.success)
            throw new Error(json.error || "Request failed");
        return json.data;
    };

    const handleSave = async () => {
        setError(null);
        setSuccess(null);
        setSaving(true);
        try {
            const data = await callSyncConfig({
                time_sync_duration: buildDuration(hopAmount, hopUnit),
                last_sync: new Date(startDate).toISOString(),
            });
            setIsEnabled(true);
            setSavedConfig(data);
            setSuccess(
                language === "zh"
                    ? "同步設定已儲存。"
                    : "Sync configuration saved.",
            );
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDisable = async () => {
        setError(null);
        setSuccess(null);
        setDisabling(true);
        try {
            await callSyncConfig({ time_sync_duration: null });
            setIsEnabled(false);
            setSavedConfig(null);
            setSuccess(language === "zh" ? "同步已停用。" : "Sync disabled.");
        } catch (e) {
            setError(e.message);
        } finally {
            setDisabling(false);
        }
    };

    return (
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* ── Status banner ──────────────────────────────────────────── */}
            {isEnabled && savedConfig ? (
                <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2.5">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        <div>
                            <p className="text-sm font-semibold text-green-800">
                                {language === "zh"
                                    ? "同步已啟用"
                                    : "Sync Active"}
                            </p>
                            <p className="text-xs text-green-600 mt-0.5">
                                {language === "zh" ? "每隔 " : "Every "}
                                <span className="font-medium">
                                    {savedConfig.time_sync_duration}
                                </span>
                                {language === "zh" ? "，從 " : " · since "}
                                <span className="font-medium">
                                    {fmtDate(new Date(savedConfig.last_sync))}
                                </span>
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleDisable}
                        disabled={disabling}
                        className="text-xs font-medium text-red-500 hover:text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-all disabled:opacity-50"
                    >
                        {disabling
                            ? language === "zh"
                                ? "停用中…"
                                : "Disabling…"
                            : language === "zh"
                              ? "停用"
                              : "Disable"}
                    </button>
                </div>
            ) : (
                <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-gray-50 border border-gray-200">
                    <div className="w-2 h-2 rounded-full bg-gray-300" />
                    <p className="text-sm text-gray-500">
                        {language === "zh"
                            ? "同步尚未啟用"
                            : "Sync is not enabled for this channel."}
                    </p>
                </div>
            )}

            {/* ── Feedback ───────────────────────────────────────────────── */}
            {error && (
                <div className="px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                    {error}
                </div>
            )}
            {success && (
                <div className="px-4 py-2.5 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700">
                    {success}
                </div>
            )}

            {/* ── Form ───────────────────────────────────────────────────── */}
            <div className="space-y-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {language === "zh"
                        ? "設定同步視窗"
                        : "Configure Sync Window"}
                </p>

                {/* Start date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {language === "zh"
                            ? "起始日期／時間"
                            : "Start Date / Time"}
                    </label>
                    <input
                        type="datetime-local"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300 bg-white"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                        {language === "zh"
                            ? "第一個同步視窗從此時間點開始計算。"
                            : "The first sync window starts counting from this point."}
                    </p>
                </div>

                {/* Hop amount + unit */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {language === "zh" ? "每次跳躍間隔" : "Hop Interval"}
                    </label>
                    <div className="flex gap-2 items-center flex-wrap">
                        <input
                            type="number"
                            min={1}
                            max={999}
                            value={hopAmount}
                            onChange={(e) =>
                                setHopAmount(
                                    Math.max(
                                        1,
                                        Math.min(999, Number(e.target.value)),
                                    ),
                                )
                            }
                            className="w-24 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300 bg-white text-center font-medium"
                        />
                        <div className="flex gap-1.5">
                            {UNIT_OPTIONS.map((u) => (
                                <button
                                    key={u.value}
                                    onClick={() => setHopUnit(u.value)}
                                    className={`px-4 py-2 text-sm font-medium rounded-xl border transition-all ${
                                        hopUnit === u.value
                                            ? "bg-orange-500 text-white border-orange-500 shadow-sm"
                                            : "bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600"
                                    }`}
                                >
                                    {language === "zh" ? u.labelZh : u.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                        {language === "zh"
                            ? "API 每次被呼叫時，若已超過一個跳躍間隔，同步視窗會自動前進。"
                            : "Each API call advances the window by one hop if a full interval has elapsed."}
                    </p>
                </div>

                <button
                    onClick={handleSave}
                    disabled={saving || !startDate}
                    className="w-full py-2.5 px-4 text-sm font-semibold text-white bg-orange-500 rounded-xl hover:bg-orange-600 active:bg-orange-700 transition-all disabled:opacity-50 shadow-sm"
                >
                    {saving
                        ? language === "zh"
                            ? "儲存中…"
                            : "Saving…"
                        : language === "zh"
                          ? "儲存設定"
                          : "Save Configuration"}
                </button>
            </div>

            {/* ── Timeline ───────────────────────────────────────────────── */}
            <div className="border-t border-gray-100 pt-4">
                <HopTimeline
                    startDate={previewStartDate}
                    amount={hopAmount}
                    unit={hopUnit}
                    language={language}
                />
            </div>

            {/* ── How it works ───────────────────────────────────────────── */}
            <div className="border-t border-gray-100 pt-4 pb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    {language === "zh" ? "運作原理" : "How It Works"}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {[
                        {
                            icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
                            en: "Window starts at your chosen date and advances automatically on each API call.",
                            zh: "視窗從您設定的日期開始，每次 API 呼叫時自動前進。",
                        },
                        {
                            icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
                            en: "Only products whose latest version existed before the current sync date are returned.",
                            zh: "只有最新版本在當前同步日期之前的產品才會被回傳。",
                        },
                        {
                            icon: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z",
                            en: "Manually pinned products always pass through regardless of the sync date.",
                            zh: "手動綁定的產品無論同步日期為何都一律顯示。",
                        },
                    ].map((card, i) => (
                        <div
                            key={i}
                            className="bg-gray-50 rounded-xl p-3.5 border border-gray-200"
                        >
                            <svg
                                className="w-4 h-4 text-orange-400 mb-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d={card.icon}
                                />
                            </svg>
                            <p className="text-xs text-gray-500 leading-relaxed">
                                {language === "zh" ? card.zh : card.en}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
